// useRestaurantData.ts
// Custom hook to handle restaurant data fetching, caching, and persistence

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getSalonesByLocalId } from '../../../../../crud/management_api/salones';
import { getAllBuiltMenusByLocalIdV2 } from '../../../../../crud/menu_api/menus';
import { localIsOpen } from '../../../../../crud/management_api/locales_caja_management';
import { convertMesaFromAPI, convertObjetoFromAPI } from '../../../Configuracion/Salones/components/utils';
import { Salon } from '../../../Configuracion/types';
import { localDB } from '../../../../../crud/localDB/localDB';
import { DisplayPrefs } from '../types';

interface UseRestaurantDataReturn {
  loading: boolean;
  salones: Salon[];
  setSalones: React.Dispatch<React.SetStateAction<Salon[]>>;
  selectedSalon: Salon | undefined;
  setSelectedSalon: (salon: Salon | undefined) => void;
  showLocalClosedModal: boolean;
  redir: number;
  hosterMode: boolean;
  setHosterMode: (mode: boolean) => void;
  displayPrefs: DisplayPrefs;
  setDisplayPrefs: (prefs: DisplayPrefs) => void;
}

export const useRestaurantData = (): UseRestaurantDataReturn => {
  const [loading, setLoading] = useState(true);
  const [salones, setSalones] = useState<Salon[]>([]);
  const [selectedSalon, setSelectedSalon] = useState<Salon | undefined>(undefined);
  const [showLocalClosedModal, setShowLocalClosedModal] = useState(false);
  const [redir, setRedir] = useState<number>(500);
  const [hosterMode, setHosterMode] = useState(false);
  const [displayPrefs, setDisplayPrefs] = useState<DisplayPrefs>({
    showNumber: true,
    showTag: false,
    showElapsed: true,
    showCharges: false,
    showWaiter: true,
    showPeopleType: 'capacity',
  });

  // Safety timeout: Force loading to false after 15 seconds
  // This effect clears itself when loading becomes false
  useEffect(() => {
    if (!loading) {
      console.log('[useRestaurantData] Loading is already false, no timeout needed');
      return;
    }
    
    console.log('[useRestaurantData] Safety timeout started - 15 seconds');
    const startTime = Date.now();
    
    const timeout = setTimeout(() => {
      setLoading(false);
      toast.warning('La carga tomó más tiempo del esperado. Si hay problemas, recarga la página.');
    }, 15000); // 15 seconds max loading time

    return () => {
      const elapsed = Date.now() - startTime;
      console.log(`[useRestaurantData] Safety timeout cleared after ${elapsed}ms (loading became false)`);
      clearTimeout(timeout);
    };
  }, [loading]); // Re-run when loading changes - this will clear the timeout when loading becomes false

  // Load persisted hosterMode preference
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const savedHosterMode = await localDB.get<boolean>('restaurant', 'hosterMode');
        if (!mounted) return;
        if (savedHosterMode !== undefined && savedHosterMode !== null) {
          setHosterMode(savedHosterMode);
        }
      } catch {
        // keep default (false)
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load persisted display preferences
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const prefs = await localDB.get<any>('restaurant', 'displayPrefs');
        if (!mounted) return;
        if (prefs) {
          // Enforce mutual exclusion: number XOR tag
          let showNumber = prefs.showNumber !== false;
          let showTag = prefs.showTag !== false;
          
          if (showNumber && showTag) {
            // Both true: keep only number
            showNumber = true;
            showTag = false;
          } else if (!showNumber && !showTag) {
            // Both false: enable number
            showNumber = true;
            showTag = false;
          }
          
          setDisplayPrefs({
            showNumber,
            showTag,
            showElapsed: prefs.showElapsed !== false,
            showCharges: prefs.showCharges !== false,
            showWaiter: prefs.showWaiter !== false,
            showPeopleType: prefs.showPeopleType || 'capacity',
          });
        } else {
          // seed defaults (number only, no tag)
          await localDB.set('restaurant', 'displayPrefs', {
            showNumber: true,
            showTag: false,
            showElapsed: true,
            showCharges: false, // first-time default disabled
            showWaiter: true,
            showPeopleType: 'capacity',
          });
        }
      } catch {
        // keep defaults
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Check local open status and load salones
  useEffect(() => {

    let mounted = true;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user?.local_id ?? 0;

    const localOpenStartTime = Date.now();
    localIsOpen(localId)
      .then((response) => {

        if (!mounted) return;
        if (response.status !== 200) {

          setRedir(response.status);
          setShowLocalClosedModal(true);
        }
      })
      .catch((error) => {

        if (!mounted) return;
        toast.error('Error obteniendo estado de apertura del local');
      });

    const salonesStartTime = Date.now();
    getSalonesByLocalId(localId)
      .then(async (response) => {
        const fetchElapsed = Date.now() - salonesStartTime;
        console.log(`[useRestaurantData] Step 2a: getSalonesByLocalId returned in ${fetchElapsed}ms`);
        if (!mounted) {
          console.warn('[useRestaurantData] Component unmounted, aborting salones processing');
          return;
        }
        
        const transformed = response.data.map((s: any) => ({
          ...s,
          mesas: s.mesas.map(convertMesaFromAPI),
          objetos: s.objetos.map(convertObjetoFromAPI),
        }));
        
        // Only set salones if this is initial load (salones is empty)
        // This prevents overwriting local state updates from WebSocket/custom events
        setSalones((prevSalones) => {
          if (prevSalones.length > 0) {
            console.log('[useRestaurantData] Salones already loaded, skipping API data to preserve local updates');
            return prevSalones; // Keep existing data, don't overwrite
          }
          console.log('[useRestaurantData] Initial salones load from API');
          return transformed;
        });

        
        // Pick initial selected salon based on fixed preference from localDB, fallback to first
        try {
          const fixedPref = await localDB.get<{ salon_id?: number; salon_name?: string }>('restaurant', 'pinnedSalon');
          
          if (!mounted) {
            console.warn('[useRestaurantData] Component unmounted after localDB.get, aborting');
            return;
          }
          
          let nextSalon: Salon | undefined = undefined;
          
          if (fixedPref) {
            // First try to find by salon_id, then by salon_name as fallback
            nextSalon = transformed.find((s: any) => 
              (fixedPref.salon_id != null && String(s.salon_id) === String(fixedPref.salon_id)) ||
              (fixedPref.salon_name && s.salon_name === fixedPref.salon_name)
            );
          }
          
          // If no fixed salon found or doesn't exist anymore, use first salon
          if (!nextSalon && transformed.length > 0) {
            nextSalon = transformed[0];
          }
          
          if (nextSalon) {
            setSelectedSalon(nextSalon);
          }
        } catch (error) {
          if (!mounted) return;
          // If localDB fails, fallback to first salon
          if (transformed.length > 0) {
            setSelectedSalon(transformed[0]);
          }
        } finally {
          if (mounted) {
  
            setLoading(false);
          } else {
            console.warn('[useRestaurantData] ⚠️ Component unmounted, NOT setting loading to false');
          }
        }
      })
      .catch((error) => {
        const elapsed = Date.now() - salonesStartTime;
        console.error(`[useRestaurantData] Step 2 FAILED: getSalonesByLocalId error after ${elapsed}ms:`, error);
        if (!mounted) {
          console.warn('[useRestaurantData] Component unmounted in catch block');
          return;
        }
        console.error('Error al obtener salones:', error);
        toast.error('Error cargando salones. Por favor, recarga la página.');
        setLoading(false);
      });
      
    return () => { 

      mounted = false; 
    };
  }, []);

  // Cache menus
  useEffect(() => {
    let mounted = true;
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const localId = user?.local_id ?? 0;

      
      const menuStartTime = Date.now();
      getAllBuiltMenusByLocalIdV2(localId, 'Restaurant')
        .then((res) => {

          if (!mounted) {
            return;
          }
          try {
            const menuData = JSON.stringify(res.data);
            localStorage.setItem('localMenus', menuData);
          } catch (storageError) {
          }
        })
        .catch((error) => {
          if (!mounted) return;
        });
    } catch (parseError) {
      console.error('[useRestaurantData] Menu caching: Error parsing user data:', parseError);
    }
    
    return () => { 
      mounted = false; 
    };
  }, []);

  return {
    loading,
    salones,
    setSalones,
    selectedSalon,
    setSelectedSalon,
    showLocalClosedModal,
    redir,
    hosterMode,
    setHosterMode,
    displayPrefs,
    setDisplayPrefs,
  };
};
